import $ from '@rover/globals/jquery';
import emitAnalyticsEvent from '@rover/rsdk/src/modules/Analytics/emitAnalyticsEvent';
import type { LastUsedLoginMethod } from '@rover/shared/js/auth/lastUsedLoginMethod';
import {
  LastUsedBadgeShown,
  LoginMethodClick,
} from '@rover/shared/js/auth/lastUsedLoginMethodEvents';

interface BadgeTracking {
  fireBadgeShown(): void;
  fireLoginMethodClick(method: LastUsedLoginMethod, redirects?: boolean): () => void;
}

/**
 * The method the sign-in form is badging, or undefined when no badge is on the
 * page. `.js-sign-in` also marks the page title, which comes first and carries
 * no method, so the selector has to name the attribute.
 */
export const getBadgedMethod = (): LastUsedLoginMethod | undefined =>
  $('.js-sign-in[data-last-used-method]').data('lastUsedMethod') || undefined;

/**
 * The badge's impression and click reporting for the Django renderer, mirroring
 * the React hooks of the same names in rsdk.
 *
 * Built once per page load, and both of its records live as long as it does:
 * counting once per page load on either renderer is what keeps their funnels
 * comparable. `badgedMethod` unset means no badge is on the page, and then
 * nothing is reported at all — a click only carries meaning next to the badge it
 * agrees or disagrees with.
 */
const createBadgeTracking = (badgedMethod?: LastUsedLoginMethod): BadgeTracking => {
  // Deduped per method, like the React tracker: the sign-in form can be
  // submitted repeatedly before it validates, and each attempt is the same
  // choice of method rather than a new one.
  const reportedMethods = new Set<LastUsedLoginMethod>();
  let hasFiredBadgeShown = false;

  return {
    // `redirects` covers the clicks that leave this page: the social links
    // follow their href and the sign-in form posts. emitAnalyticsEvent defers
    // the send to a timer the unload cancels, so those have to be stored and
    // replayed on the page the click lands on. Facebook opens an SDK dialog and
    // stays put.
    fireLoginMethodClick(method, redirects = false) {
      return () => {
        if (!badgedMethod || reportedMethods.has(method)) {
          return;
        }

        reportedMethods.add(method);
        const event = new LoginMethodClick(method, badgedMethod);
        if (redirects) {
          event.fireCrossPageEvent();
        } else {
          emitAnalyticsEvent(event);
        }
      };
    },

    // The sign-in form can start hidden behind the sign-up toggle, so the
    // impression fires when it is actually on screen — once per page either way.
    fireBadgeShown() {
      if (!badgedMethod || hasFiredBadgeShown) {
        return;
      }

      hasFiredBadgeShown = true;
      emitAnalyticsEvent(new LastUsedBadgeShown(badgedMethod));
    },
  };
};

export default createBadgeTracking;
